// /playground/routines — the per-user routine scheduler: kinds × my ledger, run-now, pause/resume,
// run history, and (dev/staging only) storm test, live knobs and a scoped reset.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

const C = {
  card: '#ffffff', border: '#d8d8dc', text: '#1d1d1f', dim: '#6e6e73', accent: '#5856d6',
  green: '#34c759', red: '#ff3b30', yellow: '#ff9500', blue: '#007aff',
  mono: "'SF Mono', 'Cascadia Code', 'JetBrains Mono', monospace",
};

const STATUS_COLOR = {
  pending: C.blue, deferred: C.yellow, running: C.accent, done: C.green,
  failed: C.red, quarantined: C.red, skipped: C.dim, active: C.green, paused: C.yellow, proposed: C.blue,
};

const btn = {
  padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: `1px solid ${C.border}`, background: C.card, color: C.text,
};
const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 14 };
const th = { textAlign: 'left', padding: '6px 8px', color: C.dim, fontWeight: 500, borderBottom: `1px solid ${C.border}` };
const td = { padding: '6px 8px', borderBottom: `1px solid ${C.border}22`, verticalAlign: 'top' };

function Badge({ value }) {
  const color = STATUS_COLOR[value] || C.dim;
  return (
    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, background: `${color}18`, color, border: `1px solid ${color}44` }}>
      {value}
    </span>
  );
}

function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

function triggerText(t) {
  if (!t) return '—';
  if (Array.isArray(t.any)) return t.any.map(triggerText).join(' or ');
  if (t.on) return `on ${t.on}${t.from ? ` from ${t.from}` : ''}`;
  if (t.every_minutes) return `every ${t.every_minutes} min${t.max_runs ? ` ×${t.max_runs}` : ''}`;
  if (t.daily) return `daily ${t.daily} ${t.tz || ''}`;
  if (t.at) return `once at ${when(t.at)}`;
  return JSON.stringify(t);
}

export default function RoutinesPage() {
  const [info, setInfo] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [newKind, setNewKind] = useState('noop');
  const [newTrigger, setNewTrigger] = useState('{"every_minutes": 5}');
  const [burst, setBurst] = useState(1000);

  const reload = useCallback(async () => {
    try {
      const [k, r, h] = await Promise.all([api.getRoutineKinds(), api.getRoutines(), api.getRoutineRuns(100)]);
      setInfo(k);
      setRoutines(r.routines || []);
      setRuns(h.runs || []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load routines');
    }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [reload]);

  const act = async (label, fn) => {
    setError(null);
    try {
      const res = await fn();
      setNote(`${label}: ${JSON.stringify(res)}`);
      reload();
    } catch (e) {
      setError(`${label}: ${e?.message || 'failed'}`);
    }
  };

  const createRoutine = () => act('create', () => {
    let trigger;
    try { trigger = JSON.parse(newTrigger); } catch { throw new Error('trigger must be JSON'); }
    return api.createRoutine(newKind, trigger);
  });

  if (!info && !error) return <div style={{ padding: 40, color: C.dim }}>Loading routines…</div>;

  const knobs = info?.knobs || {};
  const creatable = (info?.kinds || []).filter((k) => k.user_creatable);

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', fontSize: 13, color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Routines</h2>
        <button style={btn} onClick={reload}>↻ Refresh</button>
      </div>
      <p style={{ color: C.dim, marginTop: 4 }}>
        Jobs that run for you without a click — one scheduler, exactly-once, coalesced, capped.
        {' '}Scheduler loop: <b style={{ color: info?.scheduler_enabled ? C.green : C.red }}>{info?.scheduler_enabled ? 'on' : 'off'}</b>
        {knobs.global_pause ? <b style={{ color: C.red }}> · GLOBAL PAUSE</b> : null}
        {' '}· house spend today ${Number(info?.house_spend_today_usd || 0).toFixed(4)} / ${knobs.house_daily_budget_usd}
      </p>
      {error && <div style={{ ...card, color: C.red, borderColor: `${C.red}66` }}>{error}</div>}
      {note && <div style={{ ...card, color: C.dim, fontFamily: C.mono, fontSize: 11, wordBreak: 'break-all' }}>{note}</div>}

      <div style={card}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Kinds</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['kind', 'mode', 'billing', 'priority', 'max running', 'daily cap/user', 'enabled', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {(info?.kinds || []).map((k) => (
              <tr key={k.name}>
                <td style={{ ...td, fontFamily: C.mono }}>{k.name}</td>
                <td style={td}>{k.mode}</td>
                <td style={td}>{k.billing_class}</td>
                <td style={td}>{k.priority}</td>
                <td style={td}>{k.max_running}</td>
                <td style={td}>{k.daily_cap_per_user}</td>
                <td style={td}><Badge value={k.enabled ? 'active' : 'paused'} /></td>
                <td style={td}>{k.manual && <button style={btn} onClick={() => act(`run-now ${k.name}`, () => api.routineRunNow(k.name))}>Run now</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8, color: C.dim }}>
          Ledger by status: {Object.entries(info?.runs_by_status || {}).map(([s, n]) => `${s} ${n}`).join(' · ') || 'empty'}
        </div>
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>My routines</h3>
        {creatable.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={newKind} onChange={(e) => setNewKind(e.target.value)} style={btn}>
              {creatable.map((k) => <option key={k.name} value={k.name}>{k.name}</option>)}
            </select>
            <input value={newTrigger} onChange={(e) => setNewTrigger(e.target.value)}
              style={{ ...btn, fontFamily: C.mono, minWidth: 280, cursor: 'text' }}
              title='{"every_minutes": 5} | {"daily": "18:00", "tz": "Asia/Hong_Kong"} | {"at": "2026-10-11T10:00:00Z"}' />
            <button style={btn} onClick={createRoutine}>+ Create</button>
          </div>
        )}
        {routines.length === 0 ? <div style={{ color: C.dim }}>No routines yet.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['kind', 'trigger', 'status', 'next fire', 'failures', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {routines.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: C.mono }}>{r.kind}</td>
                  <td style={td}>{triggerText(r.trigger)}</td>
                  <td style={td}><Badge value={r.status} />{r.pause_reason ? <span style={{ color: C.dim }}> {r.pause_reason}</span> : null}</td>
                  <td style={td}>{when(r.next_fire_at)}</td>
                  <td style={td}>{r.consecutive_failures}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {r.status === 'active' && <button style={btn} onClick={() => act('pause', () => api.routineAction(r.id, 'pause'))}>Pause</button>}
                    {r.status === 'paused' && <button style={btn} onClick={() => act('resume', () => api.routineAction(r.id, 'resume'))}>Resume</button>}
                    {r.status !== 'done' && <button style={{ ...btn, marginLeft: 6 }} onClick={() => act('end', () => api.routineAction(r.id, 'end'))}>End</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Run history (mine, newest first)</h3>
        {runs.length === 0 ? <div style={{ color: C.dim }}>No runs yet.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['created', 'kind', 'source', 'status', 'reason', 'events', 'attempts', 'cost', 'finished', 'output'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td style={td}>{when(r.created_at)}</td>
                  <td style={{ ...td, fontFamily: C.mono }}>{r.kind}</td>
                  <td style={td}>{r.dedupe_key}</td>
                  <td style={td}><Badge value={r.status} /></td>
                  <td style={{ ...td, color: C.dim }}>{r.reason || ''}</td>
                  <td style={td}>{r.events}</td>
                  <td style={td}>{r.attempts}</td>
                  <td style={{ ...td, fontFamily: C.mono }}>{r.cost?.usd ? `$${r.cost.usd}` : ''}</td>
                  <td style={td}>{when(r.finished_at)}</td>
                  {/* run outputs are content-free counts/codes by contract */}
                  <td style={{ ...td, fontFamily: C.mono, fontSize: 10, color: C.dim, maxWidth: 360, wordBreak: 'break-all' }}>
                    {r.output && Object.keys(r.output).length ? JSON.stringify(r.output) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {info?.dev_tools && (
        <div style={{ ...card, borderColor: `${C.yellow}88` }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Dev tools (not in production)</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <span>Storm test:</span>
            <input type="number" min={1} max={10000} value={burst} onChange={(e) => setBurst(Number(e.target.value))} style={{ ...btn, width: 90, cursor: 'text' }} />
            <button style={btn} onClick={() => act('burst', () => api.routineDevBurst('noop', burst))}>Fire {burst} noop events</button>
            <span style={{ color: C.dim }}>→ must leave exactly 1 outstanding run</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <button style={btn} onClick={() => act('global_pause', () => api.routineDevKnob('global_pause', !knobs.global_pause))}>
              {knobs.global_pause ? 'Resume scheduler' : 'Global pause'}
            </button>
            <button style={btn} onClick={() => {
              const key = window.prompt(`Knob (${Object.keys(knobs).join(', ')})`);
              if (!key) return;
              const value = window.prompt(`${key} =`, String(knobs[key] ?? ''));
              if (value !== null) act(`knob ${key}`, () => api.routineDevKnob(key, value));
            }}>Set knob…</button>
            <button style={{ ...btn, color: C.red, borderColor: `${C.red}66` }} onClick={() => {
              if (window.prompt('Delete ALL my routines and runs? Type RESET') === 'RESET') act('reset', () => api.routineDevReset());
            }}>Reset my routines</button>
          </div>
          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.dim, wordBreak: 'break-all' }}>knobs: {JSON.stringify(knobs)}</div>
        </div>
      )}
    </div>
  );
}
